/**
 * Client-side search and country filter over the testimonial catalogue.
 *
 * Every quote is already in the document, so filtering never touches the server: no
 * endpoint, no query, no latency. That is a consequence of rendering the whole catalogue
 * rather than paginating it — the trade the page already made.
 *
 * The two controls narrow the same set and are combined with AND, so picking a country
 * and then typing searches inside that country rather than replacing the choice.
 *
 * Matching cells are moved in and out of the grid rather than hidden. The grid's hairlines
 * are drawn by :nth-child (three sets of them, one per column count), and display:none
 * leaves an element in the count — so hiding would keep the dividers landing on cells that
 * are no longer where the rule thinks they are. Detaching renumbers them for free.
 */
(function () {
    'use strict';

    /** Accents are how people type Portuguese and Spanish names, and how they don't. */
    function normalise(value) {
        return (value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '');
    }

    /**
     * Who said it, not what they said. Searching the quote text was tried and dropped: a
     * common word matches most of the catalogue at once, which reads as the filter having
     * done nothing. The person, the role and the company are what a reader comes looking
     * for by name.
     *
     * Country is deliberately absent even though the cell shows one: the dropdown does it
     * properly. This would have matched the raw column, where "United States" misses the
     * quotes stored as "USA" — two controls for one field, disagreeing.
     */
    function haystack(cell) {
        var parts = [];

        ['.rail-cell__person', '.rail-cell__role', '.rail-cell__company']
            .forEach(function (selector) {
                var el = cell.querySelector(selector);
                if (el) {
                    parts.push(el.textContent);
                }
            });

        return normalise(parts.join(' '));
    }

    /**
     * One dropdown: a button and a listbox standing in for the <select> that could not
     * carry a flag. Returns an object whose value() is what the reader chose, '' for all.
     *
     * Written once and used by both the area and the country filter — they are the same
     * control over different data, and only the country one has a badge to paint.
     *
     * Focus goes to the list itself while it is open and the options are pointed at with
     * aria-activedescendant, rather than each option taking focus in turn — one focus stop
     * for a control that is one choice.
     */
    function picker(root, onChange) {
        var trigger = root.querySelector('[data-picker-trigger]');
        var list = root.querySelector('[data-picker-list]');
        var badge = root.querySelector('[data-picker-badge]');
        var label = root.querySelector('[data-picker-value]');
        var options = Array.prototype.slice.call(list.querySelectorAll('[role="option"]'));

        // Held from the start so choosing "All countries" can put it back: painting the
        // badge empties it, and the globe is markup, not something to rebuild in JS. Null
        // on a picker whose options carry no glyph, which is every one but the country.
        var globe = badge ? badge.firstElementChild : null;
        var chosen = '';
        var active = 0;

        function isOpen() {
            return !list.hidden;
        }

        function setActive(index) {
            index = Math.max(0, Math.min(index, options.length - 1));
            options[active].classList.remove('is-active');
            active = index;
            options[active].classList.add('is-active');
            list.setAttribute('aria-activedescendant', options[active].id);
            // 'nearest' so the panel only scrolls when the row is actually out of view.
            options[active].scrollIntoView({ block: 'nearest' });
        }

        function open() {
            list.hidden = false;
            trigger.setAttribute('aria-expanded', 'true');
            setActive(options.indexOf(list.querySelector('[aria-selected="true"]')));
            list.focus();
        }

        function close(returnFocus) {
            list.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
            list.removeAttribute('aria-activedescendant');
            if (returnFocus) {
                trigger.focus();
            }
        }

        function choose(option) {
            options.forEach(function (el) {
                el.setAttribute('aria-selected', el === option ? 'true' : 'false');
            });

            chosen = option.getAttribute('data-value') || '';

            var count = option.getAttribute('data-count');
            label.textContent = option.getAttribute('data-label') + (count ? ' (' + count + ')' : '');

            if (badge) {
                var flag = option.getAttribute('data-flag');
                badge.textContent = '';
                if (flag) {
                    var img = document.createElement('img');
                    img.className = 'quote-filter__flag';
                    img.src = flag;
                    img.alt = '';
                    badge.appendChild(img);
                } else {
                    badge.appendChild(globe);
                }
            }

            onChange();
        }

        // Jump to a country by typing its first letters, the way a native select does.
        // The buffer resets after half a second so "ch" finds Chile and, a moment later,
        // "c" starts again at Canada.
        var typed = '';
        var typedAt = 0;

        function typeahead(key) {
            var now = Date.now();
            typed = (now - typedAt > 500 ? '' : typed) + key.toLowerCase();
            typedAt = now;

            for (var i = 0; i < options.length; i++) {
                if ((options[i].getAttribute('data-label') || '').toLowerCase().indexOf(typed) === 0) {
                    setActive(i);
                    return;
                }
            }
        }

        trigger.addEventListener('click', function () {
            if (isOpen()) {
                close(true);
            } else {
                open();
            }
        });

        // A button already opens on Enter and Space through its own click; the arrows are
        // what it does not do on its own.
        trigger.addEventListener('keydown', function (event) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                open();
            }
        });

        list.addEventListener('keydown', function (event) {
            switch (event.key) {
                case 'ArrowDown': event.preventDefault(); setActive(active + 1); break;
                case 'ArrowUp': event.preventDefault(); setActive(active - 1); break;
                case 'Home': event.preventDefault(); setActive(0); break;
                case 'End': event.preventDefault(); setActive(options.length - 1); break;
                case 'Enter':
                case ' ': event.preventDefault(); choose(options[active]); close(true); break;
                case 'Escape': event.preventDefault(); close(true); break;
                // Tab leaves the control; the list must not still be sitting over the grid.
                case 'Tab': close(false); break;
                default:
                    if (event.key.length === 1) {
                        typeahead(event.key);
                    }
            }
        });

        list.addEventListener('click', function (event) {
            var option = event.target.closest('[role="option"]');
            if (option) {
                choose(option);
                close(true);
            }
        });

        // Keeps the pointer and the arrow keys on one highlight instead of lighting two
        // rows at once when a hand joins a keyboard mid-choice.
        list.addEventListener('mouseover', function (event) {
            var option = event.target.closest('[role="option"]');
            if (option) {
                setActive(options.indexOf(option));
            }
        });

        // The trigger's own click is handled above and stops here having already toggled,
        // because root contains it.
        document.addEventListener('click', function (event) {
            if (isOpen() && !root.contains(event.target)) {
                close(false);
            }
        });

        return {
            value: function () {
                return chosen;
            },
        };
    }

    function init(input) {
        var gridId = input.getAttribute('data-quote-search');
        var grid = document.getElementById(gridId);
        if (!grid) {
            return;
        }

        var status = document.querySelector('[data-quote-search-status]');
        var cells = Array.prototype.slice.call(grid.children).map(function (cell) {
            var areas = cell.getAttribute('data-areas');

            // "Legal Operations:3|Platform:11" — where this quote sits inside each of its
            // areas. Parsed once here rather than on every keystroke.
            var order = {};
            (cell.getAttribute('data-order') || '').split('|').forEach(function (pair) {
                var at = pair.lastIndexOf(':');
                if (at > 0) {
                    order[pair.slice(0, at)] = parseInt(pair.slice(at + 1), 10);
                }
            });

            return {
                el: cell,
                text: haystack(cell),
                // Both absent on a quote that has neither on file, which is why the "All"
                // options are the only ones that can ever show it.
                country: cell.getAttribute('data-country') || '',
                areas: areas ? areas.split('|') : [],
                order: order,
            };
        });

        var shown = cells.length;

        // Keyed by what each picker filters, so a catalogue that ships only one of them —
        // no quote has a country, say — simply has one fewer key.
        var pickers = {};
        document.querySelectorAll('[data-quote-filter="' + gridId + '"]').forEach(function (root) {
            pickers[root.getAttribute('data-filter-key')] = picker(root, apply);
        });

        function report(count) {
            if (!status) {
                return;
            }

            status.textContent = count === cells.length
                ? ''
                : (count === 0 ? status.getAttribute('data-none') : status.getAttribute('data-some'))
                    .replace(':count', count)
                    .replace(':total', cells.length);
        }

        function apply() {
            var needle = normalise(input.value).trim();
            var country = pickers.country ? pickers.country.value() : '';
            // A quote carries several areas, so this one is membership rather than equality.
            var area = pickers.area ? pickers.area.value() : '';
            var idle = needle === '' && country === '' && area === '';

            var matches = idle
                ? cells
                : cells.filter(function (cell) {
                    return (country === '' || cell.country === country)
                        && (area === '' || cell.areas.indexOf(area) !== -1)
                        && (needle === '' || cell.text.indexOf(needle) !== -1);
                });

            // The page order is the best weight a quote reaches anywhere, so on its own it
            // would rank an area's list by curation that belongs to another area. Picking
            // one re-sorts by the position inside it. Safe to sort in place: `matches` is
            // only ever `cells` itself on the idle path, where area is empty.
            if (area !== '') {
                matches.sort(function (a, b) {
                    return a.order[area] - b.order[area];
                });
            }

            // Nothing to redraw when every control is clear and the whole catalogue is
            // already on screen — the common case of clearing an already-clear field.
            if (idle && shown === cells.length) {
                return;
            }

            var fragment = document.createDocumentFragment();
            matches.forEach(function (cell) {
                fragment.appendChild(cell.el);
            });

            grid.textContent = '';
            grid.appendChild(fragment);

            shown = matches.length;
            grid.classList.toggle('is-empty', matches.length === 0);
            report(matches.length);
        }

        var pending;
        input.addEventListener('input', function () {
            clearTimeout(pending);
            pending = setTimeout(apply, 120);
        });

        // A search input clears with the keyboard and with its own native button; the
        // second fires no `input` event in every browser.
        input.addEventListener('search', apply);
    }

    function start() {
        document.querySelectorAll('[data-quote-search]').forEach(init);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
